var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { reaction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import TextBlockDomHelpers from '@nedoShared/modules/notes/block/textBlock/helpers/TextBlock.dom.helpers';
import { useTextBlockFocus } from '@nedoShared/modules/notes/block/textBlock/hooks/useTextBlockFocus';
import { useTextBlockInput } from '@nedoShared/modules/notes/block/textBlock/hooks/useTextBlockInput';
import { useTextBlockKeydown } from '@nedoShared/modules/notes/block/textBlock/hooks/useTextBlockKeydown';
import { useTextBlockSelectionChange } from '@nedoShared/modules/notes/block/textBlock/hooks/useTextBlockSelectionChange';
import TextBlockConstants from '@nedoShared/modules/notes/block/textBlock/TextBlock.constants';
import TextBlockInput from '@nedoShared/modules/notes/block/textBlock/ui/textBlockInput/TextBlockInput';
import TextBlockText from '@nedoShared/modules/notes/block/textBlock/ui/textBlockText/TextBlockText';
import { getErrorPath } from '@nedoShared/modules/notes/Notes.helpers';
import Placeholder from '@nedoShared/modules/notes/placeholder/Placeholder';
import { TextBlockContext } from './TextBlock.context';
import styles from './TextBlock.module.scss';
import TextBlockStore from './TextBlock.store';
const { TEXT_UPDATE_DELAY } = TextBlockConstants;
const { focusElement } = TextBlockDomHelpers;
const TextBlock = ({ id, text, data, $section, macrosList, onUpdate, onRemove, errors, isPlain = false, }) => {
    const { t } = useTranslation();
    const [loadingPlaceholderId, setLoadingPlaceholderId] = useState(null);
    const [isBlocked, setIsBlocked] = useState(false);
    const containerRef = useRef(null);
    const updateInfo = useRef({ isLoading: false, shouldUpdateElements: false, textUpdateTimeoutId: null });
    const $store = useLocalObservable(() => new TextBlockStore(text, data));
    const hasSectionMacros = (macrosList === null || macrosList === void 0 ? void 0 : macrosList.length) > 0;
    useEffect(() => {
        if (updateInfo.current.shouldUpdateElements) {
            $store.UPDATE_ELEMENTS(text, data);
        }
        else {
            $store.UPDATE_ELEMENTS_DATA(text, data);
        }
        let lastUpdateText = $store.updatedTextOnly;
        const update = () => __awaiter(void 0, void 0, void 0, function* () {
            if (updateInfo.current.isLoading) {
                clearTimeout(updateInfo.current.textUpdateTimeoutId);
                updateInfo.current.textUpdateTimeoutId = setTimeout(update, 50);
                return;
            }
            else if (lastUpdateText !== $store.updatedTextOnly) {
                lastUpdateText = $store.updatedTextOnly;
                updateInfo.current.isLoading = true;
                updateInfo.current.shouldUpdateElements = false;
                yield onUpdate($store.updatedText);
                updateInfo.current.isLoading = false;
            }
        });
        const disposer = reaction(() => $store.updatedTextOnly, () => {
            clearTimeout(updateInfo.current.textUpdateTimeoutId);
            updateInfo.current.textUpdateTimeoutId = setTimeout(update, TEXT_UPDATE_DELAY);
        });
        return () => disposer();
    }, [text, data]);
    const requestWithBlocking = useCallback((callback) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        setIsBlocked(true);
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        clearTimeout(updateInfo.current.textUpdateTimeoutId);
        // Wait until previous update
        while (updateInfo.current.isLoading) {
            yield new Promise((resolve) => setTimeout(resolve, 50));
        }
        updateInfo.current.isLoading = true;
        updateInfo.current.shouldUpdateElements = true;
        yield callback();
        updateInfo.current.isLoading = false;
        setIsBlocked(false);
    }), []);
    const updateImmediately = useCallback((data) => __awaiter(void 0, void 0, void 0, function* () {
        yield requestWithBlocking(() => onUpdate($store.updatedText, data ? { id: data.id, value: data.value || data.fhirRef } : undefined));
    }), []);
    const updatePlaceholderText = useCallback((placeholderId, value) => __awaiter(void 0, void 0, void 0, function* () {
        if (updateInfo.current.isLoading) {
            return false;
        }
        else {
            updateInfo.current.isLoading = true;
            updateInfo.current.shouldUpdateElements = false;
            yield onUpdate($store.updatedText, { id: placeholderId, value });
            updateInfo.current.isLoading = false;
            return true;
        }
    }), []);
    const handleAddMacro = useCallback(updateImmediately, []);
    const handleChangeData = useCallback((placeholderId, { value, fhirRef }) => __awaiter(void 0, void 0, void 0, function* () {
        setLoadingPlaceholderId(placeholderId);
        yield updateImmediately({ id: placeholderId, value, fhirRef });
        setLoadingPlaceholderId(null);
    }), []);
    const handleRemove = useCallback((type) => __awaiter(void 0, void 0, void 0, function* () {
        yield requestWithBlocking(() => onRemove(type));
    }), [onRemove]);
    const handleCloseInput = useCallback((elementId) => {
        focusElement(containerRef.current, $store.deleteInput(elementId));
    }, []);
    const onKeydown = useTextBlockKeydown(containerRef, $store, hasSectionMacros, handleRemove, id, updatePlaceholderText);
    const onInput = useTextBlockInput(containerRef, $store);
    useTextBlockFocus(containerRef, id);
    useTextBlockSelectionChange(containerRef);
    const hasPlaceholder = $store.elements.length === 1 && !$store.elements[0].value;
    return (_jsx(TextBlockContext.Provider, Object.assign({ value: $store }, { children: _jsx("div", Object.assign({ ref: containerRef, className: classNames(styles.editable, isBlocked && styles.editableBlocked, hasPlaceholder && styles.editableInline, isPlain && styles.editablePlainText), contentEditable: !isBlocked && !isPlain, suppressContentEditableWarning: true, onInput: onInput, onKeyDown: onKeydown, "data-container": true }, { children: $store.elements.map((item) => {
                var _a;
                switch (item.type) {
                    case 'text':
                        return (_jsx(TextBlockText, { item: item, isBlocked: isBlocked, isPlain: isPlain, placeholder: hasPlaceholder
                                ? t(hasSectionMacros
                                    ? 'label.notes.text_block.placeholder.full'
                                    : 'label.text_something')
                                : undefined }, item.id));
                    case 'input':
                        return (_jsx(TextBlockInput, { id: item.id, value: item.value, "$section": $section, macrosList: macrosList, onAddMacro: handleAddMacro, isBlocked: isBlocked, onClose: handleCloseInput }, item.id));
                    case 'placeholder':
                        return (_jsx(Placeholder, { elementId: item.id, data: item.macroData, "$section": $section, isBlocked: isBlocked, isLoading: ((_a = item.macroData) === null || _a === void 0 ? void 0 : _a.id) === loadingPlaceholderId, onChange: handleChangeData, encounterId: $section.encounterId, isPlain: isPlain, isInvalid: errors.has(getErrorPath($section.id, id, item.macroId)) }, item.id));
                }
            }) })) })));
};
export default observer(TextBlock);
